import { _getLogger } from '@doxyme/analytics';
import { Specs, attachHandler } from '@doxyme/extensions-common';
import ExtensionCapability from '../../capability';
import type Instance from '../../instance';

const logger = _getLogger('transcription', {
  component: 'extensions',
  lib: 'bridge',
});

type Constraints = Specs.Transcription.Constraints;
type CompletedStatusPayload = Specs.Transcription.CompletedStatusPayload;
type TranscriptionOutput = Specs.Transcription.TranscriptionOutput;
type TranscriptionConfig = Specs.Transcription.Config;

const { Transcription } = Specs;

/** Default output configuration when not specified in manifest */
const DEFAULT_OUTPUT: TranscriptionOutput[] = ['transcript'];

export type TranscriptionId = string;

/**
 * Handler called when starting a transcription.
 * @param output - Array of output types requested ('transcript' and/or 'audio')
 * @returns Promise resolving to the transcription ID
 */
export type StartHandler = (
  output: TranscriptionOutput[],
) => Promise<TranscriptionId>;
export type StopHandler = (transcriptionId: TranscriptionId) => Promise<void>;
export type CancelHandler = (transcriptionId: TranscriptionId) => Promise<void>;

/**
 * Transcription allows extensions to use the transcription service within the host application.
 */
export default class TranscriptionCapability extends ExtensionCapability<Constraints> {
  static readonly id = Transcription.id;

  protected readonly startHandlers: StartHandler[] = [];

  protected readonly stopHandlers: StopHandler[] = [];

  protected readonly cancelHandlers: CancelHandler[] = [];

  /**
   * ManifestId for which transcription is currently started
   */
  #activeManifestId?: string;

  /**
   * Currently started transcriptionId
   */
  #activeTranscriptionId?: string;

  /**
   * Map of transcriptions awaiting completion
   */
  #pendingTranscriptions: Record<string, TranscriptionId[]> = {};

  /**
   * Current promise for stop transcription operation
   */
  #stopTranscriptionPromise?: Promise<void>;

  /**
   * Current promise for cancel transcription operation
   */
  #cancelTranscriptionPromise?: Promise<void>;

  constructor() {
    super(Transcription);

    this.handlers.push(
      [Transcription.Actions.Start, this.#start.bind(this)],
      [Transcription.Actions.Stop, this.#stop.bind(this)],
      [Transcription.Actions.Cancel, this.#cancel.bind(this)],
      [Transcription.Actions.State, this.#stateTranscription.bind(this)],
    );
  }

  onStart(handler: StartHandler): () => void {
    return attachHandler<StartHandler>(this.startHandlers, handler);
  }

  onStop(handler: StopHandler): () => void {
    return attachHandler<StopHandler>(this.stopHandlers, handler);
  }

  onCancel(handler: CancelHandler): () => void {
    return attachHandler<CancelHandler>(this.cancelHandlers, handler);
  }

  /**
   * Notify transcription completed
   */
  completed(payload: CompletedStatusPayload): void {
    const manifestId = Object.keys(this.#pendingTranscriptions).find((mId) =>
      this.#pendingTranscriptions[mId]?.includes(payload.transcriptionId),
    );
    if (!manifestId) {
      const errorMessage =
        'Received completed event for unregistered transcriptionId';
      logger.error(errorMessage, {
        transcriptionId: payload.transcriptionId,
        pendingTranscriptions: this.#pendingTranscriptions,
        action: 'completed',
      });
      return;
    }

    // remove transcriptionId from pending
    this.#pendingTranscriptions[manifestId] =
      this.#pendingTranscriptions[manifestId]?.filter(
        (tId) => tId !== payload.transcriptionId,
      ) ?? [];

    const instance = this.getInstanceByManifestId(manifestId);
    instance.notify([Transcription.Events.CompletedStatus, payload]);
  }

  /**
   * "Start" action handler, calls all assigned startHandlers
   * Handlers are awaited and "Started" event is triggered directly here,
   * instead of exposing a separate method.
   */
  async #start(payload: never, instance: Instance<Constraints>): Promise<void> {
    if (this.#activeManifestId) {
      const errorMessage = 'Transcription already started';
      logger.error(errorMessage, this.#getLoggerProps(instance, 'start'));
      instance.notify([
        Transcription.Events.StartStatus,
        { isSuccess: false, errorMessage },
      ]);
      return;
    }

    // Get output config from manifest, defaulting to ['transcript']
    const configs = instance.configsBy(
      Transcription.id,
    ) as TranscriptionConfig[];
    const config = configs[0];
    const output: TranscriptionOutput[] = config?.output ?? DEFAULT_OUTPUT;

    this.#activeManifestId = instance.manifest.id;
    let transcriptionId: TranscriptionId | undefined = undefined;
    try {
      for await (const handler of this.startHandlers) {
        transcriptionId = await handler(output);
      }
    } catch (error) {
      this.#activeManifestId = undefined;
      const message = 'Transcription start failed';
      logger.error(message, this.#getLoggerProps(instance, 'start', error));
      instance.notify([
        Transcription.Events.StartStatus,
        {
          isSuccess: false,
          errorMessage: this.#getErrorMessage(error, message),
        },
      ]);
      return;
    }

    // to make it simple - update with a response from the latest handler
    this.#activeTranscriptionId = transcriptionId;
    instance.notify([
      Transcription.Events.StartStatus,
      { isSuccess: true, transcriptionId: transcriptionId as string },
    ]);
  }

  /**
   * Public method to allow to cancel from within the main application (e.g. when call ends)
   */
  async stop() {
    // ignores it when there is no activeManifestId on call from the main app
    if (this.#activeManifestId === undefined) {
      return;
    }

    const instance = this.getInstanceByManifestId(this.#activeManifestId);
    return this.#stopTranscription(instance);
  }

  /**
   * "Stop" action handler.
   */
  async #stop(payload: never, instance: Instance<Constraints>): Promise<void> {
    if (!this.#isManifestIdDefined(instance, 'stop')) {
      return;
    }

    return this.#stopTranscription(instance);
  }

  /**
   * Calls all assigned stopHandlers
   * Returns existing promise to avoid multiple api calls
   */
  async #stopTranscription(instance: Instance): Promise<void> {
    return (this.#stopTranscriptionPromise ??= this.#validateAndExecuteAction(
      instance,
      'stop',
      Transcription.Events.StopStatus,
      this.stopHandlers,
      () => {
        // save to pending to wait for completion, clear ids to be able to start new transcription;
        this.#pendingTranscriptions[this.#activeManifestId!] = [
          ...(this.#pendingTranscriptions[this.#activeManifestId!] ?? []),
          this.#activeTranscriptionId!,
        ];
      },
    ).finally(() => {
      this.#stopTranscriptionPromise = undefined;
    }));
  }

  async #stateTranscription(payload: never, instance: Instance): Promise<void> {
    instance.notify([
      Transcription.Events.StateStatus,
      {
        isSuccess: true,
        state:
          this.#activeManifestId === undefined ? 'available' : 'unavailable',
      },
    ]);
  }

  /**
   * "Cancel" action handler, calls all assigned cancelHandlers
   * Handlers are awaited and "Canceled" event is triggered directly here,
   * instead of exposing a separate method.
   * Returns existing promise to avoid multiple api calls
   */
  async #cancel(
    payload: never,
    instance: Instance<Constraints>,
  ): Promise<void> {
    if (!this.#isManifestIdDefined(instance, 'cancel')) {
      return;
    }

    return (this.#cancelTranscriptionPromise ??= this.#validateAndExecuteAction(
      instance,
      'cancel',
      Transcription.Events.CancelStatus,
      this.cancelHandlers,
    ).finally(() => {
      this.#cancelTranscriptionPromise = undefined;
    }));
  }
  /**
   * Util that holds common logic for stop and cancel actions (which are really similar)
   */
  async #validateAndExecuteAction(
    instance: Instance,
    action: 'stop' | 'cancel',
    event:
      | Specs.Transcription.Events.StopStatus
      | Specs.Transcription.Events.CancelStatus,
    handlers: StopHandler[] | CancelHandler[],
    onHandlersSuccess?: () => void,
  ): Promise<void> {
    if (instance.manifest.id !== this.#activeManifestId) {
      const errorMessage = `An extension that has not started cannot ${action} transcription`;
      logger.error(errorMessage, this.#getLoggerProps(instance, action));
      instance.notify([event, { isSuccess: false, errorMessage }]);
      return;
    }

    if (!this.#activeTranscriptionId) {
      const errorMessage =
        'Transcription has not been started yet, wait for start-status event';
      logger.error(errorMessage, this.#getLoggerProps(instance, action));
      instance.notify([event, { isSuccess: false, errorMessage }]);
      return;
    }

    try {
      for await (const handler of handlers) {
        await handler(this.#activeTranscriptionId);
      }
    } catch (error) {
      const errorMessage = `Transcription ${action} failed`;
      logger.error(errorMessage, this.#getLoggerProps(instance, action, error));
      instance.notify([
        event,
        {
          isSuccess: false,
          errorMessage: this.#getErrorMessage(error, errorMessage),
          transcriptionId: this.#activeTranscriptionId,
        },
      ]);
      return;
    }

    onHandlersSuccess?.();

    instance.notify([
      event,
      { isSuccess: true, transcriptionId: this.#activeTranscriptionId },
    ]);

    this.#activeManifestId = undefined;
    this.#activeTranscriptionId = undefined;
  }

  #isManifestIdDefined(instance: Instance, action: string): boolean {
    if (this.#activeManifestId !== undefined) {
      return true;
    }

    logger.error(
      "activeManifestId is not defined, event won't be triggered",
      this.#getLoggerProps(instance, action),
    );

    return false;
  }

  /**
   * Simple util to get an error message from an error
   */
  #getErrorMessage(error: unknown, defaultMessage: string): string {
    return (error as { message?: string })?.message ?? defaultMessage;
  }

  /**
   * Util to get generic logger props
   */
  #getLoggerProps(instance: Instance, action: string, error?: unknown) {
    return {
      manifest: { id: instance.manifest.id, name: instance.manifest.name },
      activeManifestId: this.#activeManifestId,
      transcriptionId: this.#activeTranscriptionId,
      action,
      error,
    };
  }
}
